import { alpha, ColorSystemOptions } from "@mui/material";
import { gradient, grey, green, amber, red, shuttlerockBlue, purple } from "./lightColors";
import {
  sr25Gray,
  sr25Red,
  sr25Amber,
  sr25Green,
  sr25Teal,
  sr25Blue,
  sr25Purple,
  sr25Violet,
  sr25Pink,
} from "./sr25Colors";

const bg = {
  900: sr25Gray[0],
  800: sr25Gray[20],
  700: sr25Gray[50],
  600: sr25Gray[100],
  500: sr25Gray[150],
  400: sr25Gray[200],
  300: sr25Gray[300],
  200: sr25Gray[400],
  100: sr25Gray[500],
  50: sr25Gray[600],

  A900: alpha(sr25Gray[0], 0.9),
  A800: alpha(sr25Gray[0], 0.8),
  A700: alpha(sr25Gray[0], 0.7),
  A600: alpha(sr25Gray[0], 0.6),
  A500: alpha(sr25Gray[0], 0.5),
  A400: alpha(sr25Gray[0], 0.4),
  A300: alpha(sr25Gray[0], 0.3),
  A200: alpha(sr25Gray[0], 0.2),
  A150: alpha(sr25Gray[0], 0.15),
  A100: alpha(sr25Gray[0], 0.1),
  A50: alpha(sr25Gray[0], 0.05),
  A30: alpha(sr25Gray[0], 0.03),
  A20: alpha(sr25Gray[0], 0.02),
};
const fg = {
  900: sr25Gray[980],
  800: sr25Gray[950],
  700: sr25Gray[900],
  600: sr25Gray[800],
  500: sr25Gray[700],
  400: sr25Gray[600],
  300: sr25Gray[500],
  200: sr25Gray[400],
  100: sr25Gray[300],
  50: sr25Gray[200],

  A900: alpha(sr25Gray[900], 0.9),
  A800: alpha(sr25Gray[900], 0.8),
  A700: alpha(sr25Gray[900], 0.7),
  A600: alpha(sr25Gray[900], 0.6),
  A500: alpha(sr25Gray[900], 0.5),
  A400: alpha(sr25Gray[900], 0.4),
  A300: alpha(sr25Gray[900], 0.3),
  A200: alpha(sr25Gray[900], 0.2),
  A150: alpha(sr25Gray[900], 0.15),
  A100: alpha(sr25Gray[900], 0.1),
  A50: alpha(sr25Gray[900], 0.05),
  A30: alpha(sr25Gray[900], 0.03),
  A20: alpha(sr25Gray[900], 0.02),
};

// `LightColorScheme` defines the colour palette used in light mode
export const LightColorScheme: ColorSystemOptions = {
  palette: {
    mode: "light",
    // 2025 New DS Colors
    sr25Gray,
    sr25Blue,
    sr25Purple,
    sr25Red,
    sr25Amber,
    sr25Green,
    sr25Teal,
    sr25Violet,
    sr25Pink,
    bg,
    fg,
    surface: {
      main: bg[900],
      a000: alpha(bg[900], 0),
      dim: bg[800],
      receded: bg[800],
      container: bg[700],
      drawer: bg[800],
      elevated: bg[900],
      inset: bg[600],
      inverted: fg[900],
      hover: bg[800],
      dropdown: bg[900],
      overlay: alpha(sr25Gray[990], 0.05), // opacity value matches action.hoverOpacity
      overlayDim: alpha(sr25Gray[990], 0.03),
      overlayDimmer: alpha(sr25Gray[990], 0.02),
      overlayStrong: alpha(sr25Gray[990], 0.08),
      overlayStronger: alpha(sr25Gray[990], 0.15),
      contrastText: fg[900],
      contrastTextDim: fg[700],
      contrastTextDimmer: fg[500],
      contrastTextDimmest: fg[400],
    },
    // Special semantic token group for foreground colors
    // on a surface
    // ex: on.surface, on.primaryContainer
    on: {
      surface: fg[900], // = surface.contrastText
      surfaceDim: fg[700], // = surface.contrastTextDim
      surfaceDimmer: fg[400], // = surface.contrastTextDimmer
      surfaceDimmest: fg[200], // = surface.contrastTextDimmest
      surfaceInverted: bg[900],
      primary: sr25Blue[50], // = primary.contrastText
      secondary: sr25Gray[50], // = secondary.contrastText
      tertiary: sr25Purple[50], // = tertiary.contrastText
    },
    primary: {
      main: sr25Blue[500],
      light: sr25Blue[400],
      dark: sr25Blue[600],
      contrastText: sr25Blue[50],
      container: alpha(sr25Blue[400], 0.2),
      // deprecated
      hover: shuttlerockBlue[800],
      active: shuttlerockBlue[800],
      background: shuttlerockBlue[100],
    },
    secondary: {
      main: sr25Gray[500],
      light: sr25Gray[400],
      dark: sr25Gray[600],
      contrastText: sr25Gray[50],
      container: alpha(sr25Gray[300], 0.2),
    },
    tertiary: {
      main: sr25Purple[700],
      light: sr25Purple[600],
      dark: sr25Purple[800],
      contrastText: sr25Purple[50],
      container: alpha(sr25Purple[500], 0.2),
    },
    error: {
      main: red[700],
      light: red[500],
      dark: red[800],
      contrastText: grey.A50,
      foreground: sr25Red[600],

      // deprecated
      background: red[100],
    },
    info: {
      main: shuttlerockBlue[700],
      light: shuttlerockBlue[50],
      dark: shuttlerockBlue[800],
      contrastText: grey.A50,
      // deprecated
      background: shuttlerockBlue[100],
    },
    success: {
      main: green[500],
      light: green[100],
      active: green[600],
      dark: green[700],
      contrastText: grey.A50,
      container: alpha(sr25Green[100], 0.2),
      border: sr25Green[400],

      // deprecated
      background: green[100],
    },
    warning: {
      main: amber[700],
      light: amber[500],
      dark: amber[800],
      contrastText: grey.A50,
      container: alpha(sr25Amber[300], 0.1),
      // deprecated
      background: amber[50],
    },
    background: {
      default: grey[100],
      paper: grey[50],
      secondary: grey[200],
      card: grey[100],
      cardHover: grey[200],
      cardAlt: grey[50],
      cardAltHover: grey.A50,
    },
    foreground: {
      main: grey[50],
      secondary: grey[100],
      tertiary: grey[200],
      hover: grey[300],
    },
    stroke: {
      main: sr25Gray[150],
      hover: sr25Gray[300],
      active: sr25Blue[500],
      strong: sr25Gray[400],
      transparent: alpha(sr25Gray[990], 0.2),
      transparentDim: alpha(sr25Gray[990], 0.1),
      transparentStrong: alpha(sr25Gray[990], 0.3),
    },
    text: {
      primary: fg[900], // on.surface
      primaryAlt: fg[700], // on.surfaceDim
      secondary: fg[400], // on.surfaceDimmer
      disabled: fg[200], // on.surfaceDimmest
    },
    divider: sr25Gray[150],
    gradient: {
      main: gradient.main,
      light: gradient.hover,
      background: gradient.background,
      blueToPurple: gradient.blueToPurple,
      purpleToBlue: gradient.purpleToBlue,
      pinkToOrange: gradient.pinkToOrange,
      contrastText: grey.A50,
      blackToTransparent: gradient.blackToTransparent,
    },
    automation: {
      background: purple[100],
      light: purple[50],
      main: purple[500],
      dark: purple[700],
    },
    instructions: {
      light: grey[50],
      main: purple[500],
      dark: grey[800],
    },
    action: {
      hover: fg.A50,
      active: fg[700],
      disabled: fg[100],
      disabledBackground: fg.A50,
      hoverOpacity: 0.05,
    },
    gender: {
      all: sr25Green[500],
      female: sr25Purple[500],
      male: sr25Blue[500],
    },
    brand: {
      creativex: "#ff425b",
    },
    grey,
    selection: alpha(sr25Blue[500], 0.3),
    Alert: {
      errorStandardBg: red[50],
      errorStandardColor: red[600],
      errorStandardIconColor: red[500],

      infoStandardBg: shuttlerockBlue[50],
      infoStandardColor: shuttlerockBlue[800],
      infoStandardIconColor: shuttlerockBlue[700],

      successStandardBg: green[50],
      successStandardColor: green[600],
      successStandardIconColor: green[500],

      warningStandardBg: amber[50],
      warningStandardColor: amber[800],
      warningStandardIconColor: amber[500],

      secondaryStandardBg: grey[100],
      secondaryStandardColor: grey[900],
      secondaryStandardTitleColor: grey[700],
      secondaryStandardIconColor: grey[700],

      automationStandardBg: purple[100],
      automationStandardColor: purple[900],
      automationStandardTitleColor: purple[700],
      automationStandardIconColor: purple[700],

      // applying the opacity on the background all while using a hacky css to apply gradient on boarder reduced our options greatly, hence the use of hsl.
      instructionsStandardBg: `linear-gradient(100.23deg, hsl(209, 100%, 95%) -0.53%, hsl(271, 100%, 95%) 70.81%, hsl(306, 100%, 95%) 115.3%) padding-box, ${gradient.blueToPurple} border-box`,
      instructionsStandardBorder: "2px solid transparent",
      instructionsStandardColor: grey[800],
      instructionsStandardActionColor: grey[800],
      instructionsStandardIconColor: grey[800],

      successFilledColor: grey.A50,
      automationFilledBg: purple[500],
      automationFilledColor: grey.A50,
      secondaryFilledBg: grey.A500,
      secondaryFilledColor: grey.A50,
      instructionsFilledBg: gradient.blueToPurple,
      instructionsFilledColor: grey[50],

      infoOutlinedBg: shuttlerockBlue[50],
      infoOutlinedBorder: shuttlerockBlue[500],
      infoOutlinedColor: shuttlerockBlue[700],
      infoOutlinedIconColor: shuttlerockBlue[700],

      successOutlinedBg: sr25Green[50],
      successOutlinedBorder: sr25Green[500],
      successOutlinedColor: sr25Green[700],
      successOutlinedIconColor: sr25Green[700],

      warningOutlinedBg: amber[50],
      warningOutlinedBorder: amber[700],
      warningOutlinedColor: amber[700],
      warningOutlinedIconColor: amber[700],

      secondaryOutlinedBorder: grey[500],
      secondaryOutlinedColor: grey[800],
      secondaryOutlinedIconColor: grey[500],

      automationOutlinedBorder: purple[500],
      automationOutlinedColor: purple[800],
      automationOutlinedIconColor: purple[500],

      instructionsOutlinedBg: `linear-gradient(white, white) padding-box, ${gradient.blueToPurple} border-box`,
      instructionsOutlinedBorder: "2px solid transparent",
      instructionsOutlinedColor: grey[800],
      instructionsOutlinedActionColor: shuttlerockBlue[700],
      instructionsOutlinedIconColor: shuttlerockBlue[700],
    },
    Button: {
      textPrimaryColor: sr25Blue[500],
      textSecondaryColor: fg[700],

      containedPrimaryContrastText: sr25Blue[20],
      containedPrimaryBg: sr25Blue[500],
      containedPrimaryHoverBg: sr25Blue[600],
      containedPrimaryActiveBg: sr25Blue[700],

      containedSecondaryBg: fg.A50,
      containedSecondaryHoverBg: fg.A100,
      containedSecondaryActiveBg: fg.A200,

      containedAutomationContrastText: sr25Purple[50],
      containedAutomationBg: sr25Purple[700],
      containedAutomationHoverBg: sr25Purple[800],
      containedAutomationActiveBg: sr25Purple[900],

      containedOverlayDisabledContrastText: grey[400],
      containedOverlayDisabledBg: sr25Gray[700],

      containedErrorContrastText: sr25Red[50],
      containedErrorBg: sr25Red[600],
      containedErrorHoverBg: sr25Red[700],
      containedErrorActiveBg: sr25Red[800],

      containedWarningContrastText: sr25Gray[0],
      containedWarningBg: sr25Amber[400],
      containedWarningHoverBg: sr25Amber[500],
      containedWarningActiveBg: sr25Amber[600],

      containedSuccessContrastText: sr25Gray[0],
      containedSuccessBg: sr25Green[400],
      containedSuccessHoverBg: sr25Green[500],
      containedSuccessActiveBg: sr25Green[600],

      containedInheritColor: "inherit",
      containedInheritBg: fg.A100,
      containedInheritHoverBg: fg.A200,
      containedInheritActiveBg: fg.A50,

      outlinedPrimaryHoverColor: grey.A50,
      outlinedPrimaryHoverBg: sr25Blue[800],
      outlinedPrimaryBorder: sr25Blue[400],
      outlinedPrimaryDisabledBorder: sr25Gray[400],

      outlinedSecondaryBg: "transparent",
      outlinedSecondaryBorder: alpha(fg[800], 0.2),
      outlinedSecondaryHoverBg: alpha(fg[800], 0.02),
      outlinedSecondaryHoverBorder: alpha(fg[800], 0.3),
      outlinedSecondaryDisabledBorder: alpha(fg[800], 0.1),
    },
    Card: {
      bg: fg.A50,
    },
    Checkbox: {
      color: sr25Blue[50],
      checkedReadonlyColor: fg[600],
      checkedLabelColor: fg.A900,
    },
    Chip: {
      m3PrimaryColor: sr25Blue[50],
      m3PrimaryHoverBg: sr25Blue[500],
      m3SecondaryColor: fg[500],
      m3SecondaryHoverBg: fg.A100,
      m3DefaultColor: fg[500],
      m3DefaultHoverBg: fg.A100,
      m3ErrorColor: sr25Red[700],
      m3ErrorHoverBg: sr25Red[50],
      m3InfoColor: sr25Blue[500],
      m3InfoHoverBg: sr25Blue[50],
      m3WarningColor: sr25Amber[600],
      m3WarningHoverBg: sr25Amber[50],
      m3SuccessColor: sr25Green[500],
      m3SuccessHoverBg: sr25Green[100],
      m3ClickableBg: "rgba(0, 0, 0, 0.15)",

      filledSuccessColor: green[500],
      filledSuccessBg: green[100],
      filledWarningColor: amber[700],
      filledWarningBg: amber[100],
      filledErrorColor: red[700],
      filledErrorBg: red[100],
      // Because secondary was used with error colours, ive duplicated them here (default already has the grey colours if need be)
      filledSecondaryColor: red[700],
      filledSecondaryBg: red[100],
      filledDefaultColor: fg[800],
      filledDefaultBg: fg.A100,
      xsFilledDefaultColor: fg.A900,
      xsFilledDefaultBg: fg.A100,
    },
    Dialog: {
      bg: grey[50],
      color: grey[900],
    },
    LinearProgress: {
      primaryColor: grey[200],
      primaryBarColor: shuttlerockBlue[500],
      successBarColor: green[500],
      errorBarColor: red[500],
    },
    Menu: {
      bg: bg[900], // surface.dropdown
      color: fg[900], // on.surface
      boxShadow: `0px 5px 15px 0px rgba(17, 17, 17, 0.1), 0 0 0 1px rgba(17, 17, 17, 0.05)`,
    },
    Radio: {
      color: grey[300],
      checkedReadonlyColor: grey[600],
      checkedLabelColor: grey.A900,
    },
    Switch: {
      defaultColor: sr25Gray[0],
      defaultDisabledColor: sr25Gray[300],
      primaryDisabledColor: alpha(sr25Blue[600], 0.38),
      secondaryDisabledColor: alpha(sr25Gray[500], 0.38),
      errorDisabledColor: alpha(red[700], 0.38),
      infoDisabledColor: alpha(shuttlerockBlue[700], 0.38),
      successDisabledColor: alpha(green[500], 0.38),
      warningDisabledColor: alpha(amber[700], 0.38),
      thumbColor: sr25Gray[0],
      trackOffBg: fg.A100,
      trackOffBorder: sr25Gray[200],
      trackOffHoverBg: fg.A200,
      trackOffHoverBorder: sr25Gray[300],
      trackOffActiveBg: fg.A300,
      trackOffActiveBorder: sr25Gray[400],
      trackOnPrimaryBg: sr25Blue[600],
      trackOnPrimaryHoverBg: sr25Blue[500],
      trackOnPrimaryActiveBg: sr25Blue[700],
      trackOnSuccessBg: sr25Green[500],
      trackOnSuccessHoverBg: sr25Green[400],
      trackOnSuccessActiveBg: sr25Green[600],
      focusRingColor: alpha(sr25Blue[500], 0.3),
      focusRingWidth: "3px",
    },
    Select: {
      color: grey.A800,
    },
    FormLabel: {
      color: grey.A900,
    },
    FilledInput: {
      color: fg[900],
      bg: alpha(sr25Gray[990], 0.05),
      hoverBg: alpha(sr25Gray[990], 0.08),
      disabledBg: alpha(sr25Gray[990], 0.03),
      placeholderColor: fg[500],
      placeholderOpacity: 0.5,
    },
    OutlinedInput: {
      color: fg[900],
      bg: sr25Gray[0],
      border: sr25Gray[150], // stroke.main
      disabledBg: bg[700],
      disabledBorder: sr25Gray[150],
      hoverBorder: sr25Gray[300], // stroke.hover
      focusBorder: sr25Blue[500], // stroke.active
      boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.024)",
      placeholderColor: fg[500],
      placeholderOpacity: 0.5,
    },
    Slider: {
      bg: grey.A600,
      primaryTrack: shuttlerockBlue[400],
      railColor: grey.A200,
      markBg: grey.A200,
      valueLabelBg: grey[600],
    },
    SvgIcon: {
      primaryColor: shuttlerockBlue[500],
      successColor: green[500],
      errorColor: red[500],
      automationColor: purple[700],
    },
    Tabs: {
      trackColor: grey[200],
    },
    Tab: {
      color: grey[600],
      hoverBg: alpha(sr25Gray[990], 0.05), // surface.overlay
      activeBg: alpha(sr25Gray[990], 0.08), // surface.overlayStrong
      selectedBg: alpha(sr25Gray[990], 0.08), // surface.overlayStrong
      selectedColor: shuttlerockBlue[500],
    },
    Table: {
      rowHoverBg: grey.A100,
      headBg: grey.A100,
      bodyColor: grey.A800,
      border: grey[200],
    },
    TableCell: {
      border: sr25Gray[100],
    },
    Tooltip: {
      bg: grey[800],
      color: grey[50],
      boxShadow: "0px 4px 44px rgba(0, 0, 0, 0.25)",
    },
  },
};
